import type { JSX, ReactElement, Ref, RefObject } from 'react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type { TextInputProps } from './TextInput';
import type { ClassNameProp } from '../common-types';
import type { ArrayIfMultiElseNullable, InfiniteScrollStore } from '../../lib';
import { intl, intlFmt, showError, useLayerDeferedVisibility } from '../../lib';
import { useCombobox, useMultipleSelection } from 'downshift';
import type { ComboboxDropdownMenuProps } from './internal';
import {
  ComboboxDropdownMenu,
  ComboboxInputElement,
  DEFAULT_COMBOBOX_LAYER_PLACEMENT
} from './internal';
import _ from 'lodash';

/**
 * Props of a {@link LiveSearch} component.
 */
export interface LiveSearchProps<Item, Variant extends 'single' | 'multi'>
  extends Pick<
      TextInputProps,
      'disabled' | 'debounceWait' | 'label' | 'placeholder' | 'labelOnTop'
    >,
    ClassNameProp,
    Partial<
      Pick<ComboboxDropdownMenuProps<Item>, 'renderItem' | 'renderEmptyItems'>
    > {
  /**
   * Mapping function for `Item` to distinguish items.
   * @param item the item to map
   */
  itemToKey: (item: Item) => string;
  /**
   * Mapping function for `Item` to create a human-readable label
   * @param item the item to map
   */
  itemToString: (item: Item) => string;
  /**
   * Callback handler for when an item is selected.
   * @param selectedItem the selected item
   */
  onChange: (selectedItem: ArrayIfMultiElseNullable<Variant, Item>) => void;
  /**
   * Ajax call used to load the selectable items when the input element's content changes.
   *
   * NOTE: This needs to be a cancellable ajax request which automatically cancels its previous request(s) when starting
   * a new request if you want it to behave in a sane way.
   * @param query
   */
  loadItems: (query?: string) => Promise<Item[]>;
  /**
   * Initial value to search by.
   * Setting/changing this will automatically trigger a request to loadItems with the initialSearchValue as the query.
   */
  initialSearchValue?: string;
  /**
   * When loadItems returns only a single item in its result set, this determines if that item
   * should automatically be selected or not.
   */
  automaticallySelectSingleResult?: boolean;
  /**
   * CSS classes to add to the downshift menu.
   */
  menuClassName?: string;
  /**
   * The min length of the query string, that determines if loadItems actually gets
   * executed or not. The default value is 1.
   * Setting this value to 0 will make to component automatically trigger the search when
   * it is rendered and display a list of all unfiltered items when it is focused.
   */
  minQueryLength?: number;

  /**
   * A boolean indicating that the TextInput value should be cleared after selecting an item.
   * This is useful when we are adding a new item to the list and want to clear the input field.
   * @default false
   */
  clearTextInputOnSelectItem?: boolean;

  /**
   * Container were the dropdown layer should be rendered to
   */
  container?: RefObject<HTMLElement | null>;
  /**
   * The variant of the `LiveSearch`, either 'single' or 'multi'. This determines if one or multiple items can be selected.
   * Depending on the variant, the type of the `value` and the parameter of `onChange` also change.
   */
  variant: Variant;
  /**
   * The currently selected item(s). Depending on the variant, this is either a single item or an array of items.
   * Setting/changing this will override the selection in the component with the given value.
   * This is useful if you want to control the selection from the outside or reset the selection by setting this to null/[].
   */
  value?: ArrayIfMultiElseNullable<Variant, Item>;
  /**
   * Defines if the `SearchIcon` is displayed at the left corner of the Inputfield.
   */
  showSearchIcon?: boolean;
  /**
   * Custom render function for the tags in multi select variant. If not provided, the default is to just show the string representation of the item.
   * @param item the item to render as a tag
   */
  renderTag?: (item: Item) => ReactElement;
  /**
   * Provide an infinite scroll store to enable `InfiniteScrollStore` for the dropdown menu.
   */
  infiniteScrollStore?: InfiniteScrollStore<Item, unknown>;
  /** Makes mobile screen-reader users able to focus individual dropdown options. */
  focusableOptions?: boolean;
}

/**
 * Interface of a handler which gives parent component the control of the inner state
 */
export type LiveSearchElement = HTMLInputElement & {
  /**
   * clear selection
   */
  deselectCurrent: () => void;
  /**
   * Reset the selection, clear loaded items and input field
   */
  reset: () => void;
};

/**
 * Select style component which is meant to be used as a live AJAX search, which changes
 * its selectable items depending on the result of the AJAX query.
 *
 * @param properties a {@link LiveSearchProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
function LiveSearchComponent<
  Item extends { tagItem?: () => JSX.Element } | string | Record<string, any>,
  Variant extends 'single' | 'multi'
>({
  itemToKey,
  itemToString,
  onChange,
  loadItems,
  debounceWait = 1000,
  label,
  disabled,
  placeholder,
  renderItem,
  renderEmptyItems,
  initialSearchValue,
  className = '',
  menuClassName = '',
  automaticallySelectSingleResult = false,
  minQueryLength = 1,
  ref,
  labelOnTop,
  clearTextInputOnSelectItem = false,
  container,
  variant,
  value,
  showSearchIcon,
  renderTag,
  infiniteScrollStore,
  focusableOptions = false,
  ...props
}: Readonly<LiveSearchProps<Item, Variant>> & {
  ref?: Ref<HTMLInputElement>;
}): React.JSX.Element {
  type ItemType = Item;
  // TODO: maybe add some caching? e.g. result of the last 20 queries
  const [loading, setLoading] = useState(false);
  const [selectableItems, setSelectableItems] = useState<Array<Item>>([]);
  // local state of the text entered into the input field
  const [inputValue, setInputValue] = useState<string | undefined>('');
  // indicator that loadItems was called at least once before.
  const [wasLoaded, setWasLoaded] = useState(false);
  // indicator of first search. In case of initial search which results in one item,
  // we want it to be automatically selected independently of automaticallySelectSingleResult
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  // we remember the most recent query, to prevent querying nonsensical querying
  const [previousSearchQuery, setPreviousSearchQuery] = useState<
    string | undefined
  >(undefined);
  const getSelectedItemsForVariant = useCallback(
    (
      theVariant: Variant,
      valueToConvert: ArrayIfMultiElseNullable<Variant, Item> | undefined
    ): ItemType[] => {
      if (theVariant === 'single') {
        const single = (valueToConvert as unknown) as
          | ItemType
          | null
          | undefined;
        return single ? [single] : [];
      }
      return ((valueToConvert as unknown) as ItemType[] | undefined) ?? [];
    },
    []
  );
  const {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    setSelectedItems
  } = useMultipleSelection<ItemType>({
    getA11yRemovalMessage({ removedSelectedItem }) {
      return intlFmt('common.item.removed', itemToString(removedSelectedItem));
    },
    // this is necessary so that removeSelectedItem knows how to announce the change to the screen reader
    itemToString,
    selectedItems: getSelectedItemsForVariant(variant, value),
    onSelectedItemsChange: async ({ selectedItems: ms }) => {
      // Convert the selection back into the expected onChange payload
      if (variant === 'single') {
        const first = ms?.[0] ?? null;
        const single = (first as unknown) as ArrayIfMultiElseNullable<
          Variant,
          Item
        >;
        onChange(single);
      } else {
        const arr = (ms ?? []) as ItemType[];
        const multi = (arr as unknown) as ArrayIfMultiElseNullable<
          Variant,
          Item
        >;
        onChange(multi);
      }
    }
  });
  // keep a ref to selectedItems so inputValueChangeCallback doesn't need it as a dependency
  // (otherwise every selection change recreates the debounced fn and cancels the pending call)
  const selectedItemsRef = useRef(selectedItems);
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  // loading callbacks
  const inputValueChangeCallback = useCallback(
    (
      inputValue: string | undefined,
      previousSearchQuery: string | undefined
    ) => {
      if (infiniteScrollStore && infiniteScrollStore?.reset) {
        infiniteScrollStore?.reset();
      }
      if (
        (inputValue ?? '').length >= minQueryLength &&
        (previousSearchQuery === undefined ||
          inputValue !== previousSearchQuery)
      ) {
        setPreviousSearchQuery(inputValue);
        setLoading(true);
        const selectedKeys = new Set(
          selectedItemsRef.current.map(s => itemToKey(s))
        );
        const excludeSelected = (c: Item) => !selectedKeys.has(itemToKey(c));
        loadItems(inputValue)
          .then(items => {
            const filtered = items.filter(excludeSelected);
            if (
              variant === 'multi' &&
              infiniteScrollStore &&
              filtered.length < items.length
            ) {
              // Some items were filtered out — load one more page so the list
              // isn't too short. Don't set selectableItems yet so that both
              // pages appear together (Combobox shows LoadMask while empty).
              loadItems(inputValue)
                .then(moreItems => {
                  setSelectableItems(moreItems.filter(excludeSelected));
                  setWasLoaded(true);
                  setIsFirstSearch(false);
                })
                .catch(showError)
                .finally(() => setLoading(false));
            } else {
              setSelectableItems(filtered);
              setWasLoaded(true);
              setIsFirstSearch(false);
              setLoading(false);
            }
          })
          .catch(e => {
            showError(e);
            setLoading(false);
          });
      } else if (inputValue === '') {
        setPreviousSearchQuery(inputValue);
        setSelectableItems([]);
        setWasLoaded(false);
      }
    },
    [infiniteScrollStore, loadItems, minQueryLength, itemToKey, variant]
  );
  const debouncedInputValueChangeCallback = useMemo(
    () => _.debounce(inputValueChangeCallback, debounceWait),
    [debounceWait, inputValueChangeCallback]
  );

  const deselectCurrent = () => {
    if (selectedItem) {
      selectItem((null as unknown) as Item);
    }
  };

  const reset = () => {
    // after resetting, the previousSearchQuery is treated as being the initial value, meaning undefined
    inputValueChangeCallback('', undefined);
    setInputValue('');
    selectItem((null as unknown) as Item);
  };

  // imperativeHandle to put the input element into the forwardedRef
  // to have access to it from the outside
  const inputElemRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({
    ...(inputElemRef.current as HTMLInputElement),
    deselectCurrent,
    reset
  }));

  useEffect(() => {
    // when the initialSearchValue changes, we override the value of the input
    // element with the new initialSearchValue, and immediately do the change callback
    // but only if the initialSearchValue is different from what's already entered in the input element
    if (initialSearchValue && initialSearchValue !== inputValue) {
      inputValueChangeCallback(initialSearchValue, undefined);
      setInputValue(initialSearchValue);
    }
    // cheating dependencies since we don't want to depend on the `inputValue`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchValue, inputValueChangeCallback]);

  useEffect(() => {
    // if the minQueryLength is explicitly set to 0, we trigger the search as soon as the component is rendered
    // so that the list of results is displayed immediately when opened without the need to type
    if (minQueryLength === 0 && !initialSearchValue) {
      inputValueChangeCallback('', undefined);
    }
  }, [initialSearchValue, inputValueChangeCallback, minQueryLength]);

  // handler for when the input value changes
  // this is a workaround to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
  // (instead of using useCombobox.stateChangeTypes.InputChange in the switch further down)
  // TODO: remove the workaround once Downshift fixed the issue on their side
  const onInputChange = useCallback(
    (value: string | undefined) => {
      debouncedInputValueChangeCallback(value, previousSearchQuery);
      setInputValue(value);
    },
    [debouncedInputValueChangeCallback, previousSearchQuery]
  );

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    selectedItem,
    toggleMenu,
    closeMenu,
    openMenu
  } = useCombobox({
    inputValue,
    items: selectableItems,
    defaultIsOpen: false,
    initialIsOpen: false,
    getA11yStatusMessage({ isOpen, resultCount, previousResultCount }) {
      if (isOpen) {
        if (!resultCount) {
          return intl('common.search.noentry');
        }
        if (resultCount !== previousResultCount) {
          return intlFmt('common.item.navigate', resultCount);
        }
      }
      return '';
    },
    // "type" is missing in the types of onStateChange which is why this ignore is needed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            if (clearTextInputOnSelectItem) {
              reset();
            } else {
              setInputValue(itemToString(selectedItem));
            }
            if (variant === 'multi') {
              // For multi, update selection via addSelectedItem which triggers
              // useMultipleSelection.onSelectedItemsChange and that will call onChange.
              addSelectedItem(selectedItem);
            } else {
              // For single, set the selected items array. useMultipleSelection
              // will convert this change and call onChange once.
              setSelectedItems([selectedItem]);
            }
          }
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          break;
        default:
          break;
      }
    },
    scrollIntoView(item) {
      item?.scrollIntoView({ block: 'nearest' });
    }
  });
  // effect for automatically selecting an item when the selectableItems only contains 1 item
  // or, in case of automatic selection is not enabled, when the initial search results in 1 item
  useEffect(() => {
    if (
      variant === 'single' &&
      selectableItems.length === 1 &&
      (automaticallySelectSingleResult ||
        (initialSearchValue &&
          isFirstSearch &&
          inputValue === initialSearchValue))
    ) {
      // do the same as when clicking an item
      const itemToSelect = selectableItems[0];
      selectItem(itemToSelect);
      setInputValue(itemToString(itemToSelect));
      onChange(
        (itemToSelect as unknown) as ArrayIfMultiElseNullable<Variant, Item>
      );
      // make sure to close the menu after selecting the item
      closeMenu();
    }
  }, [
    automaticallySelectSingleResult,
    closeMenu,
    initialSearchValue,
    inputValue,
    isFirstSearch,
    itemToString,
    onChange,
    selectItem,
    selectableItems,
    variant
  ]);

  const {
    layerProps,
    triggerProps,
    renderLayer,
    triggerBounds
  } = useLayerDeferedVisibility({
    isOpen: isOpen,
    container: container?.current ?? undefined,
    onOutsideClick: () => closeMenu(),
    ...DEFAULT_COMBOBOX_LAYER_PLACEMENT
  });

  return (
    <>
      {isOpen &&
        renderLayer(
          <ComboboxDropdownMenu<Item>
            triggerBounds={triggerBounds}
            className={
              infiniteScrollStore
                ? 'infinite-scrolling-livesearch ' + menuClassName
                : menuClassName
            }
            loading={loading}
            selectableItems={selectableItems}
            selectedItems={selectedItems}
            highlightedIndex={highlightedIndex}
            itemToKey={itemToKey}
            renderItem={renderItem ?? itemToString}
            layerProps={layerProps}
            isOpen={isOpen}
            getMenuProps={getMenuProps}
            getItemProps={getItemProps}
            wasLoaded={wasLoaded}
            renderEmptyItems={renderEmptyItems}
            hideClearIcon={true}
            infiniteScrollStore={infiniteScrollStore}
            focusableOptions={focusableOptions}
            next={() => {
              setLoading(true);
              const selectedKeys = new Set(
                selectedItems.map(s => itemToKey(s))
              );
              const excludeSelected = (c: Item) =>
                !selectedKeys.has(itemToKey(c));
              loadItems(inputValue)
                .then(items => {
                  const filtered = items.filter(excludeSelected);
                  if (variant === 'multi' && filtered.length < items.length) {
                    loadItems(inputValue)
                      .then(moreItems => {
                        setSelectableItems(moreItems.filter(excludeSelected));
                      })
                      .catch(showError)
                      .finally(() => setLoading(false));
                  } else {
                    setSelectableItems(filtered);
                    setLoading(false);
                  }
                  setWasLoaded(true);
                  setIsFirstSearch(false);
                })
                .catch(e => {
                  showError(e);
                  setLoading(false);
                });
            }}
          />
        )}
      <ComboboxInputElement<Item>
        {...props}
        ref={inputElemRef}
        className={className}
        triggerProps={triggerProps}
        variant={variant}
        showSearchIcon={showSearchIcon ?? true}
        showExpandTool={false}
        selectedItems={selectedItems}
        isOpen={isOpen}
        label={label}
        itemToString={itemToString}
        disabled={disabled}
        toggleMenu={toggleMenu}
        openMenu={openMenu}
        getLabelProps={getLabelProps}
        getInputProps={options =>
          getInputProps({
            ...options,
            onBlur: event => {
              const target = event.relatedTarget;
              if (
                focusableOptions &&
                target instanceof HTMLElement &&
                target.closest('[role="listbox"]')?.id ===
                  inputElemRef.current?.getAttribute('aria-controls')
              ) {
                (event as React.FocusEvent<HTMLInputElement> & {
                  preventDownshiftDefault?: boolean;
                }).preventDownshiftDefault = true;
                return;
              }
              options?.onBlur?.(event);
            }
          })
        }
        placeholder={placeholder}
        getComboboxProps={getComboboxProps}
        elementClickBehavior="openMenu"
        onInputChange={onInputChange}
        removeSelectedItem={variant === 'multi' ? removeSelectedItem : reset}
        labelOnTop={labelOnTop}
        getDropdownProps={getDropdownProps}
        renderTag={renderTag}
      />
    </>
  );
}

export const LiveSearch = LiveSearchComponent;
